import { z } from 'zod';
import { expenseClassificationSchema } from './bookkeeping.js';
import { isoDateTimeSchema, uuidSchema } from './common.js';
/**
 * The tax contract.
 *
 * One rule governs every shape in this file: **the rule engine decides, the
 * model explains.** So a decision's outcome fields and its explanation are
 * different things with different provenance, and the explanation is always
 * optional. A screen that has the numbers but not the prose is fine. A screen
 * that has the prose but not the numbers is not a thing this API can produce.
 */
export const TAX_COMPONENTS = ['gst', 'hst', 'pst', 'qst', 'exempt', 'zero_rated'];
export const taxComponentSchema = z.enum(TAX_COMPONENTS);
/** One link in the reasoning: a rule, an override, a default, or the floor. */
export const taxChainStepSchema = z.object({
    step: z.string(),
    tier: z.string().nullish(),
    ruleId: uuidSchema.nullish(),
    ruleName: z.string().nullish(),
    /** The citation, e.g. "ITA 67.1". Quoted, never composed. */
    authority: z.string().nullish(),
    description: z.string().nullish(),
    sourceUrl: z.string().nullish(),
    pack: z.string().nullish(),
    packVersion: z.string().nullish(),
    packVerified: z.boolean().nullish(),
    effectiveFrom: z.string().nullish(),
    classification: z.string().nullish(),
    itcEligiblePercent: z.union([z.number(), z.string()]).nullish(),
    overrideId: uuidSchema.nullish(),
    reason: z.string().nullish(),
    detail: z.string().nullish(),
});
export const taxDecisionSchema = z.object({
    id: uuidSchema,
    transactionId: uuidSchema,
    transactionLineId: uuidSchema,
    tier: z.string(),
    ruleId: uuidSchema.nullable(),
    ruleName: z.string().nullable(),
    authority: z.string().nullable(),
    overrideId: uuidSchema.nullable(),
    pack: z.string().nullable(),
    expenseClassification: expenseClassificationSchema,
    /** Numeric on the wire because it is a percentage, not money. */
    itcEligiblePercent: z.number(),
    /** Money. A string, always. */
    itcAmountMinor: z.string(),
    taxComponent: taxComponentSchema.nullable(),
    /** False when nobody has checked the rule set this came from. */
    fromVerifiedPack: z.boolean(),
    warnings: z.array(z.string()),
    chain: z.array(taxChainStepSchema),
    decidedOn: z.string(),
    createdAt: isoDateTimeSchema,
    /**
     * Plain English, written by a model from the chain above.
     *
     * Null is a perfectly good value: an unexplained decision is still a correct
     * decision. Nothing in this system reads this field back.
     */
    explanation: z.string().nullable(),
    explanationModel: z.string().nullable(),
});
export const taxRulePackSchema = z.object({
    id: uuidSchema,
    code: z.string(),
    name: z.string(),
    version: z.string(),
    sourceUrl: z.string().nullable(),
    publishedOn: z.string().nullable(),
    verifiedAt: isoDateTimeSchema.nullable(),
    verificationNote: z.string().nullable(),
    ruleCount: z.number().int(),
    rateCount: z.number().int(),
});
/**
 * The three sentences an owner wants on a Monday morning.
 *
 * Each is a query over the ledger, not a claim. `itcAtRiskMinor` is separate
 * from `itcAvailableMinor` on purpose: telling somebody they can claim $8,420
 * when $900 of it rests on receipts with no supplier registration is how a
 * pleasant number becomes a reassessment.
 */
export const taxInsightsSchema = z.object({
    periodFrom: z.string(),
    periodTo: z.string(),
    itcAvailableMinor: z.string(),
    itcAtRiskMinor: z.string(),
    taxPaidMinor: z.string(),
    recordsMissingTax: z.number().int(),
    recordsFromUnverifiedPack: z.number().int(),
    capitalTreatedAsExpense: z.number().int(),
    suppliersWithoutTaxNumber: z.number().int(),
    unverifiedPacks: z.array(z.string()),
});
export const taxInsightsQuerySchema = z.object({
    from: z.string(),
    to: z.string(),
});
/** Who checked the rules, and against what. "Verified" alone is not a record. */
export const verifyPackRequestSchema = z.strictObject({
    note: z.string().trim().min(10).max(500),
});
export const explainDecisionRequestSchema = z.strictObject({
    /** Regenerate even if the decision already carries an explanation. */
    force: z.boolean().optional(),
});
/** A quarter, for the period picker. Calendar quarters; CRA filing periods
 *  follow `hst_periods`, which this release does not touch. */
export function currentQuarter(today) {
    const year = today.getUTCFullYear();
    const quarter = Math.floor(today.getUTCMonth() / 3);
    const start = new Date(Date.UTC(year, quarter * 3, 1));
    const end = new Date(Date.UTC(year, quarter * 3 + 3, 0));
    return { from: start.toISOString().slice(0, 10), to: end.toISOString().slice(0, 10) };
}
/**
 * How to describe a claim whose rule set nobody has checked.
 *
 * Never silently. The whole reason `tax_rule_packs.verified_at` exists is that
 * a note in a README is not a control, and a number on a screen with no
 * caveat is the same failure one layer up.
 */
export function verificationCaveat(insights) {
    if (insights.unverifiedPacks.length === 0)
        return null;
    return `These figures use ${insights.unverifiedPacks.join(' and ')}, which nobody has checked against the legislation yet. Confirm the rates before filing.`;
}
//# sourceMappingURL=tax.js.map